/**
 * **The utility advisor** — rank every legal macro-move ({@link generateMacroMoves}) by the
 * value of its **projected end-state** (a position swap only — no full command adjudication at
 * 1 ply) plus the attack leg's own closed-form EV, evaluated through the pluggable weighted evaluator (`evaluator.ts`). Returns
 * the top-`k` plans with a human-readable reasoning breakdown: feature contributions, hit %,
 * EV damage — everything a GM (or a later autoplay driver) needs to see WHY a plan ranked where
 * it did.
 *
 * This is intentionally a **1-ply** advisor — it does not look past its own next command, does
 * not model the opponent's reply, and does not run the referee. `search.ts`'s expectiminimax is
 * the layer this feeds, as both leaf evaluator and move-ordering seed.
 */

import { sideOf, type Cell, type Combatant, type EncounterState, type WeaponProfile } from "@lancer-vtt/domain";
import { SEED_OVERLAY, VARIABLE_DAMAGE_TYPE_OPTIONS, type LoadedOverlay } from "@lancer-vtt/rules";
import type { Compendium } from "@lancer-vtt/compendium";
import { generateMacroMoves, type MacroMove } from "./generator";
import { evaluateAttackEv, type AttackEv } from "./ev";
import { DEATHMATCH_TERMS, evaluateState, materialTerm, type EvaluationBreakdown, type FeatureTerm } from "./evaluator";

/**
 * `evaluateAttackEv` for a `damage.type: "variable"` weapon — ranking-time only, an ESTIMATE.
 * `evaluateAttackEv` itself throws without a concrete `damageTypeOverride` (`ev.ts`'s
 * documented scope: it doesn't model the resolver's own choose-prompt). Scoring a variable
 * weapon as a flat zero (never trying any type) would make it tie "do nothing" on EVERY
 * pairing — and since {@link generateMacroMoves} always emits the "no attack" plan BEFORE any
 * attack pairing at the same destination, a stable sort keeps "do nothing" ranked ABOVE a
 * zero-scored attack forever, so the advisor would NEVER choose to fire a variable weapon at
 * all. Instead, argmax over the three resolvable types (the SAME set `promptPolicy.ts`'s
 * `decideVariableDamageType` argmaxes at ANSWER time) for a realistic best-case estimate. The
 * submitted `RequestAttack` intent itself carries no override (`generator.ts` never resolves
 * damage type) — so the REAL resolver still raises the genuine `needs_decision`, and
 * `promptPolicy.ts` answers it independently; this estimate only steers RANKING, never the wire.
 */
function bestVariableAttackEv(args: { state: EncounterState; attacker: Combatant; target: Combatant; weapon: WeaponProfile; weaponRef: string; overlay: LoadedOverlay }): AttackEv {
  const { state, attacker, target, weapon, weaponRef, overlay } = args;
  let best: AttackEv | undefined;
  for (const dtype of VARIABLE_DAMAGE_TYPE_OPTIONS) {
    const ev = evaluateAttackEv({ state, attacker, target, weapon, weaponRef, damageTypeOverride: dtype, overlay });
    if (!best || ev.expectedDamage > best.expectedDamage) best = ev;
  }
  // `VARIABLE_DAMAGE_TYPE_OPTIONS` is non-empty (`["kinetic","energy","explosive"]`), so `best` is always set.
  return best!;
}

export type RankedMacroMove = {
  move: MacroMove;
  /** The plan's total utility score (position-swap evaluation + the attack leg's EV-damage bonus, when present). Higher is better for the acting side. */
  score: number;
  /** The projected end-state's evaluation (position swap only — no damage applied). */
  positionBreakdown: EvaluationBreakdown;
  /** The attack leg's closed-form EV, when `move.attack` is set. */
  attackEv?: AttackEv;
  /**
   * A one-line human-readable summary for display (`label` + the key numbers).
   *
   * **Mirrored in the browser: `frontend/src/lib/encounter/advisorPanel.ts`'s `planReasoning`
   * composes this same line so the advisor panel can name cells the way the board's ruler does.**
   * Rephrase this (or its rounding) and that mirror must move in lockstep — `advisorPanel.test.ts`
   * derives its expectation from this very string at runtime, so a drift reds rather than passing
   * unnoticed. See {@link MacroMove.label}'s note for the other half of the pair.
   */
  reasoning: string;
};

export type AdvisorArgs = {
  state: EncounterState;
  actorId: string;
  compendium: Compendium;
  overlay?: LoadedOverlay;
  /** The weighted feature terms to evaluate projected end-states with; defaults to {@link DEATHMATCH_TERMS}. */
  terms?: readonly FeatureTerm[];
  /** How many top-ranked plans to return; default 5. */
  k?: number;
  /** Forwarded to {@link generateMacroMoves}'s destination pruning; default 10. */
  maxDestinations?: number;
};

/**
 * Clone `state` with `actorId` repositioned to `destination` — a pure position swap, no combat
 * resolution (the advisor's "no full adjudication needed at 1 ply" stance). Exported (Phase
 * 4.1a, `./search.ts`) as the ONE shared "apply a macro-move's deterministic (movement) leg"
 * primitive — the search projects both the searching actor's own move AND (via
 * {@link "./activationPick" withHypotheticalActivation}) a not-yet-activated opponent's reply
 * through this SAME function, never a second hand-rolled copy.
 */
export function withPositionSwap(state: EncounterState, actorId: string, destination: Cell): EncounterState {
  const actor = state.combatants[actorId];
  if (!actor) return state;
  const patched: Combatant = { ...actor, pos: destination };
  return { ...state, combatants: { ...state.combatants, [actorId]: patched } };
}

/** Rank every legal macro-move for `actorId`, top-`k` first — the advisor's one entry point. */
export function rankMacroMoves(args: AdvisorArgs): RankedMacroMove[] {
  const { state, actorId, compendium, overlay = SEED_OVERLAY, terms = DEATHMATCH_TERMS, k = 5, maxDestinations = 10 } = args;
  const actor = state.combatants[actorId];
  if (!actor) return [];
  const side = sideOf(actor);

  const moves = generateMacroMoves({ state, actorId, compendium, overlay, maxDestinations });

  const ranked: RankedMacroMove[] = moves.map((move) => {
    const projected = withPositionSwap(state, actorId, move.destination);
    const positionBreakdown = evaluateState({ state: projected, side, overlay }, terms);

    let attackEv: AttackEv | undefined;
    let attackBonus = 0;
    if (move.attack) {
      const target = state.combatants[move.attack.target.id];
      const attacker = projected.combatants[actorId] ?? actor;
      if (target) {
        // The attacker's frozen weapon profile for this leg: a loadout instance key resolves off
        // `base.weapons`; a melee-action ref (`act_ram`/…) has no loadout entry — reuse
        // `checkAttackLegal`'s own resolution seam is unnecessary here since the generator already
        // proved legality; we only need the profile for the EV math, so look it up the same way.
        const weapon = attacker.base.weapons?.find((w) => (w.instanceId ?? w.id) === move.attack!.weapon);
        if (weapon && weapon.damage.type === "variable") {
          attackEv = bestVariableAttackEv({ state: projected, attacker, target, weapon, weaponRef: move.attack.weapon, overlay });
        } else if (weapon) {
          attackEv = evaluateAttackEv({ state: projected, attacker, target, weapon, weaponRef: move.attack.weapon, overlay });
        } else if (move.attack.weapon.startsWith("act_")) {
          // A synthetic melee-action profile (Ram/Grapple/Improvised Attack) carries no loadout
          // entry — the generator resolved it via `enumerateActions`, not `base.weapons`; the EV
          // toolkit needs the actual frozen profile the resolver would fire, which only
          // `@lancer-vtt/rules`' internal `MELEE_ACTION_ATTACKS` table holds (unexported by
          // design — a synthetic, not a loadout fact). v1 leaves these attacks unscored on
          // damage (still ranked on position alone); a later phase can widen `evaluateAttackEv`
          // to accept a pre-resolved profile directly instead of re-deriving that table here.
        }
        if (attackEv) {
          const maxHp = target.base.maxHp || 1;
          attackBonus = materialTerm.weight * (attackEv.expectedDamage / maxHp);
        }
      }
    }

    const score = positionBreakdown.total + attackBonus;
    const reasoning = attackEv
      ? `${move.label} — ${(attackEv.pHit * 100).toFixed(0)}% to hit (${(attackEv.pCrit * 100).toFixed(0)}% crit), E[dmg]=${attackEv.expectedDamage.toFixed(1)}, score=${score.toFixed(2)}`
      : `${move.label} — score=${score.toFixed(2)}`;

    return { move, score, positionBreakdown, ...(attackEv ? { attackEv } : {}), reasoning };
  });

  ranked.sort((a, b) => b.score - a.score);
  return ranked.slice(0, k);
}
